import { useState } from 'react';
import { submitForm } from '../../services/apiService';
import { useNavigate } from 'react-router-dom';

const UserForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    postcode: '',
    address: '',
    phoneNumber: '',
    county: '',
    location: '',
    preferredContact: [],
    propertyType: '',
    approxArea: '',
    existingSurface: [],
    condition: '',
    serviceRequired: '',
    preferredStart: '',
    leadOrigin: '', // Location/place of the lead
    generatedBy: '', // Name of person who generated the lead
    priority: 'medium',
    quoteAmount: '',
    additionalNotes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      const arrayName = name.includes('preferredContact') ? 'preferredContact' : 
                       'existingSurface';
      setFormData(prev => ({
        ...prev,
        [arrayName]: checked 
          ? [...prev[arrayName], value]
          : prev[arrayName].filter(item => item !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    if (!formData.propertyType) {
      newErrors.propertyType = 'Please select property type';
    }
    
    if (!formData.serviceRequired) {
      newErrors.serviceRequired = 'Please select a service';
    }
    
    // NEW: Validation for lead origin and generated by
    if (!formData.leadOrigin.trim()) {
      newErrors.leadOrigin = 'Please enter the lead location/place';
    }
    
    if (!formData.generatedBy.trim()) {
      newErrors.generatedBy = 'Please enter the name of who generated this lead';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('📤 SUBMITTING FORM DATA:', formData);
      
      const submissionData = {
        ...formData,
        serviceRequired: formData.serviceRequired
      };
      
      const result = await submitForm(submissionData);
      console.log('✅ FORM SUBMISSION SUCCESS:', result);
      
      navigate('/success', { 
        state: { 
          submissionId: result.id,
          customerName: formData.customerName 
        } 
      });
      
    } catch (error) {
      console.error('❌ FORM SUBMISSION ERROR:', error);
      
      let errorMessage = error.message;
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('400')) {
        errorMessage = 'Invalid form data. Please check all required fields.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      alert(`Error submitting form: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    { value: 'phone', label: 'Phone Call', icon: '📞' },
    { value: 'email', label: 'Email', icon: '✉️' },
    { value: 'whatsapp', label: 'WhatsApp', icon: '💬' }
  ];

  const propertyTypes = [
    { value: 'residential', label: 'Residential', icon: '🏠' },
    { value: 'commercial', label: 'Commercial', icon: '🏢' }
  ];

  const surfaces = [
    { value: 'concrete', label: 'Concrete', icon: '🏗️' },
    { value: 'tarmac', label: 'Tarmac', icon: '🛣️' },
    { value: 'gravel', label: 'Gravel', icon: '⚪' },
    { value: 'grass', label: 'Grass', icon: '🌱' }
  ];

  const conditions = [
    { value: 'good', label: 'Good', color: 'text-green-600' },
    { value: 'fair', label: 'Fair', color: 'text-yellow-600' },
    { value: 'poor', label: 'Poor', color: 'text-red-600' }
  ];

  const timelines = [
    { value: 'asap', label: 'ASAP (Immediate Start)' },
    { value: '1 month', label: 'Within 1 Month' },
    { value: '1-3 months', label: '1-3 Months' },
    { value: '6 months', label: 'Within 6 Months' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-red-600' }
  ];

  const services = [
    { value: '', label: 'Select a service...', icon: '🔧' },
    { value: 'resin driveway', label: 'Resin Driveway', icon: '🛣️' },
    { value: 'resin patio', label: 'Resin Patio', icon: '🏞️' },
    { value: 'resin path', label: 'Resin Path', icon: '🚶' },
    { value: 'resin driveway+path', label: 'Resin Driveway + Path', icon: '🛣️🚶' },
    { value: 'resin driveway+patio', label: 'Resin Driveway + Patio', icon: '🛣️🏞️' },
    { value: 'resin driveway+patio+path', label: 'Resin Driveway + Patio + Path', icon: '🛣️🏞️🚶' },
    { value: 'procelain slabs', label: 'Procelain Slabs', icon: '🧱' },
    { value: 'block', label: 'Block Paving', icon: '🧱' },
    { value: 'tarmac', label: 'Tarmac', icon: '🛣️' },
    { value: 'conservatory', label: 'Conservatory', icon: '🏠' },
    { value: 'extension', label: 'Extension', icon: '🏗️' },
    { value: 'artificial grass', label: 'Artificial Grass', icon: '🌿' },
    { value: 'grass', label: 'Natural Grass', icon: '🌱' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 rounded-full mb-4">
            <span className="text-2xl text-white">📝</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Project Inquiry Form
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Complete the form below to receive a personalised quote for the Lead
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Progress Indicator */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Get Your Quote</span>
              </div>
              <div className="text-sm bg-white/20 px-3 py-1 rounded-full">
                Step 1 of 1
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            {/* Customer Details Section */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">1</span>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Contact Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                      errors.customerName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.customerName && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.customerName}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                      errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                      errors.phoneNumber ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="+44 123 456 7890"
                  />
                  {errors.phoneNumber && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Enter your location/town"
                  />
                </div>

                {/* Address */}
                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Street Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Enter your complete address"
                  />
                </div>

                {/* Postcode & County */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Postcode
                  </label>
                  <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    placeholder="e.g., SW1A 1AA"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    County
                  </label>
                  <input
                    type="text"
                    name="county"
                    value={formData.county}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Enter your county"
                  />
                </div>
              </div>
            </div>

            {/* Lead Source Information - UPDATED TO TEXT INPUTS */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">2</span>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Lead Information
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Lead Origin - Location/Place */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Lead Location/Place <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="leadOrigin"
                    value={formData.leadOrigin}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                      errors.leadOrigin ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Enter the location or place of this lead"
                  />
                  {errors.leadOrigin && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.leadOrigin}
                    </p>
                  )}
                </div>

                {/* Generated By - Person's Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Generated By (Name) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="generatedBy"
                    value={formData.generatedBy}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                      errors.generatedBy ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Enter the name of who generated this lead"
                  />
                  {errors.generatedBy && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.generatedBy}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Preferred Contact Method */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">3</span>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Preferred Contact Method
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {contactMethods.map((method) => (
                  <label
                    key={method.value}
                    className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                      formData.preferredContact.includes(method.value)
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      name={`preferredContact-${method.value}`}
                      value={method.value}
                      checked={formData.preferredContact.includes(method.value)}
                      onChange={handleChange}
                      className="hidden"
                    />
                    <span className="text-2xl">{method.icon}</span>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {method.label}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Services Required */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">4</span>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Services Required <span className="text-red-500">*</span>
                </h2>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Select Service
                </label>
                <select
                  name="serviceRequired"
                  value={formData.serviceRequired}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                    errors.serviceRequired ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {services.map((service) => (
                    <option key={service.value} value={service.value}>
                      {service.icon} {service.label}
                    </option>
                  ))}
                </select>
                {errors.serviceRequired && (
                  <p className="text-red-500 text-sm flex items-center gap-1 mt-2">
                    <span>⚠️</span>
                    {errors.serviceRequired}
                  </p>
                )}
              </div>
            </div>

            {/* Property Details */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">5</span>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Project Details
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Property Type */}
                <div className="space-y-4">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Property Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {propertyTypes.map((type) => (
                      <label
                        key={type.value}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          formData.propertyType === type.value
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="propertyType"
                          value={type.value}
                          checked={formData.propertyType === type.value}
                          onChange={handleChange}
                          className="hidden"
                        />
                        <span className="text-2xl">{type.icon}</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {type.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.propertyType && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span>
                      {errors.propertyType}
                    </p>
                  )}
                </div>

                {/* Area, Condition & Priority */}
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Approximate Area (SQM)
                    </label>
                    <input
                      type="number"
                      name="approxArea"
                      value={formData.approxArea}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                      placeholder="Enter area in square meters"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Current Condition
                    </label>
                    <select
                      name="condition"
                      value={formData.condition}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    >
                      <option value="">Select current condition</option>
                      {conditions.map((condition) => (
                        <option key={condition.value} value={condition.value}>
                          {condition.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Priority */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Priority Level
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    >
                      <option value="">Select priority</option>
                      {priorities.map((priority) => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Existing Surface */}
              <div className="mt-6 space-y-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Existing Surface Type
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {surfaces.map((surface) => (
                    <label
                      key={surface.value}
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.existingSurface.includes(surface.value)
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        name={`existingSurface-${surface.value}`}
                        value={surface.value}
                        checked={formData.existingSurface.includes(surface.value)}
                        onChange={handleChange}
                        className="hidden"
                      />
                      <span className="text-xl">{surface.icon}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {surface.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Project Timeline & Quote Amount */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">6</span>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Project Timeline & Budget
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Preferred Start Date
                  </label>
                  <select
                    name="preferredStart"
                    value={formData.preferredStart}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                  >
                    <option value="">When would you like to start?</option>
                    {timelines.map((timeline) => (
                      <option key={timeline.value} value={timeline.value}>
                        {timeline.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quote Amount */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Expected Budget/Quote Amount (£)
                  </label>
                  <input
                    type="number"
                    name="quoteAmount"
                    value={formData.quoteAmount}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Enter expected budget"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">7</span>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Additional Information
                </h2>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Project Notes & Requirements
                </label>
                <textarea
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 resize-vertical"
                  rows="5"
                  placeholder="Please provide any additional details about your project, specific requirements, or questions you may have..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="bg-gray-50 dark:bg-gray-700/50 -mx-8 -mb-8 px-8 py-6 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <p>🔒 Your information is secure and confidential</p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 min-w-[200px]"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </div>
                  ) : (
                    'Submit Project Inquiry'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-2xl mb-2">🚀</div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Fast Tracking</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Quick View to keep track </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-2xl mb-2">💎</div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Import/export</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">easy filekeeping and online storage</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-2xl mb-2">📊</div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Analytics</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">detailed estimate & showcase</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserForm;